import PostHog from "posthog-js"

import { SdkSigners } from "#/shared/services/sdk"
import { AccountCreationStatus, OnboardingFlow } from "#/shared/types"

import createDappAsyncThunk from "../asyncThunk"
import {
  resetAccount,
  setAccountCreationStatus,
  setHasModifiedUsername,
  setUserAuthId,
  setUsername,
} from "../slices/account"
import { resetBorrowState } from "../slices/borrow"
import { resetBridge } from "../slices/bridge"
import { resetDonationState } from "../slices/donation"
import { resetPortal } from "../slices/portal"
import { resetMats } from "../slices/rewards"
import { resetStoreState } from "../slices/store"
import {
  resetUI,
  setCanSendTransactions,
  setHasAccess,
  setHasLoadedAccountData,
} from "../slices/ui"
import {
  resetWallet,
  setConnectedChainId,
  setConnectedWallet,
} from "../slices/wallet"

import { createSession } from "./session"

// Don't use this directly, use loginAndFetchAccount instead
export const loginAccount = createDappAsyncThunk(
  "account/loginAccount",
  async (_, { getState, dispatch, extra: { sdk } }) => {
    const { isConnected } = getState().wallet

    // TODO: canSendTransactions and hasAccess shoudl be refactored, maybe merged
    dispatch(setCanSendTransactions(false))
    dispatch(setHasLoadedAccountData(false))

    if (!isConnected) {
      dispatch(setHasLoadedAccountData(true))
      return null
    }

    const account = await sdk.mezo.api.account.getAccount()

    /// If account was created already and we have a cookie => we have access
    if (account) {
      // Identify user by Supabase Auth ID
      PostHog.identify(account.authId)

      dispatch(setUserAuthId(account.authId))
      dispatch(setUsername(account.username))
      dispatch(setHasModifiedUsername(account.hasModifiedUsername))
      dispatch(setCanSendTransactions(true))
      dispatch(setHasAccess(true))
      dispatch(setAccountCreationStatus(AccountCreationStatus.Created))
      dispatch(setHasLoadedAccountData(true))

      return account
    }

    // If backend throws error the account may be there but the cookie may be lost
    // try to restore the session
    const { payload: sessionExists } = await dispatch(createSession())

    // Session was restored, we have a valid cookie
    if (sessionExists) {
      // Try to restore the account
      const restoredAccount = await sdk.mezo.api.account.getAccount()

      dispatch(setCanSendTransactions(!!restoredAccount))
      dispatch(setHasAccess(!!restoredAccount))

      if (restoredAccount !== null) {
        // Identify user by Supabase Auth ID
        PostHog.identify(restoredAccount.authId)

        dispatch(setUserAuthId(restoredAccount.authId))
        dispatch(setUsername(restoredAccount.username))
        dispatch(setHasModifiedUsername(restoredAccount.hasModifiedUsername))
      }

      dispatch(setHasLoadedAccountData(true))

      return restoredAccount
    }

    dispatch(setCanSendTransactions(false))
    dispatch(setHasAccess(false))
    dispatch(setHasModifiedUsername(false))
    dispatch(setHasLoadedAccountData(true))

    return null
  },
)

export const connectWalletAccount = createDappAsyncThunk(
  "account/connectWalletAccount",
  async (
    {
      name,
      signerEthersV5,
      signerEthersV6,
      signerMezo,
      isBitcoinSigner,
    }: {
      name?: string | null
    } & SdkSigners,
    { dispatch, extra: { sdk } },
  ) => {
    await sdk.connectSigner({
      signerEthersV5,
      signerEthersV6,
      signerMezo,
      isBitcoinSigner,
    })

    if (!sdk.mezo.wallet) {
      throw new Error("Wallet should be already connected.")
    }

    const { evmAddress, btcAddress } = sdk.mezo.wallet

    dispatch(setConnectedWallet({ evmAddress, btcAddress, name }))
  },
)

export const updateAccountCreationStatus = createDappAsyncThunk(
  "account/updateAccountCreationStatus",
  async (
    {
      chainId,
      accountExists,
    }: { chainId: number | null; accountExists: boolean },
    { getState, dispatch },
  ) => {
    const { flow: onboardingFlow } = getState().onboarding

    if (onboardingFlow === OnboardingFlow.SIGN_UP && !accountExists) {
      // If we are in Sign up flow we only need wallet connection
      // at this point
      dispatch(setAccountCreationStatus(AccountCreationStatus.Connected))
    } else {
      // If we are in Sign in flow we need to prompt user to sign
      // message to create session
      dispatch(setAccountCreationStatus(AccountCreationStatus.Signing))
    }

    dispatch(setConnectedChainId(chainId))
  },
)

export const disconnectWalletAccount = createDappAsyncThunk(
  "account/disconnectWalletAccount",
  async (_, { dispatch, extra: { sdk } }) => {
    await sdk.disconnectSigner()
    dispatch(resetBorrowState())
    dispatch(resetWallet())
    dispatch(resetMats())
    dispatch(resetPortal())
    dispatch(resetAccount())
    dispatch(resetUI())
    dispatch(resetDonationState())
    dispatch(resetStoreState())
    dispatch(resetBridge())
  },
)

export const checkAvaiableUsername = createDappAsyncThunk(
  "account/checkAvaiableUsername",
  async (username: string, { extra: { sdk } }) => {
    const usernameAvailable =
      await sdk.mezo.api.account.checkAvailableUsername(username)

    return usernameAvailable
  },
)
